import { Component, Injector, Input, OnDestroy, OnInit, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { EMPTY, of, Subscription } from 'rxjs';
import { concatMap, map, switchMap, tap } from 'rxjs/operators';
import { SelectDialogComponent } from '../../../common/component/select-dialog/select-dialog.component';
import { SortFormComponent } from '../../../common/component/sort-form/sort-form.component';
import { SelectionItem } from '../../../core/component/select/select.component';
import { getTranslation } from '../../../core/helper/translation';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { FakeGuidService } from '../../../core/service/fake-guid.service';
import { RoomType, RoomTypeToString } from '../../../dwelling-inspection/model/dwelling-inspection-room';
import { CustomProperty } from '../../model/custom-property';
import { Dwelling } from '../../model/dwelling';
import { ObjectComponentCategory } from '../../model/object-component-category';
import { Premises } from '../../model/premises';
import { Room } from '../../model/room';
import { CustomPropertyService } from '../../service/data/custom-property.service';
import { ObjectComponentCategoryService } from '../../service/data/object-component-category.service';
import { ObjectComponentService } from '../../service/data/object-component.service';
import { LanguageService } from '../../../core/service/language.service';

@Component({
    selector: 'app-rooms',
    templateUrl: './rooms.component.html',
    styleUrls: ['./rooms.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class RoomsComponent implements OnInit, OnDestroy {
  constructor(
    private _componentCategoryService: ObjectComponentCategoryService,
    private _componentService: ObjectComponentService,
    private _cf: FormControlExFactory,
    private _fb: FormBuilder,
    private _injector: Injector,
    private _actionWindow: ActionWindowService,
    private readonly _languageService: LanguageService,
    private _customPropertyService: CustomPropertyService
  ) {}

  private _forms: FormArray;
  @Input() set forms(value: AbstractControl) {
    this._forms = value as FormArray;
  }
  get forms(): FormArray {
    return this._forms;
  }
  @Input() rootForm: FormGroup;
  @Input() rentalObject: Dwelling | Premises;
  @Output() componentAdded = new EventEmitter<void>();

  addedComponent() {
    this.componentAdded.emit();
  }

  componentCategories: ObjectComponentCategory[];
  private _subscription = new Subscription();
  private _dialogSubscription = new Subscription();
  private _initSubscription = new Subscription();
  rooms: Room[] = [];
  roomsOrderSidebarOpen: boolean = false;

  roomTypes: SelectionItem[] = [
    new SelectionItem(RoomTypeToString(RoomType.Bedroom), RoomType.Bedroom),
    new SelectionItem(RoomTypeToString(RoomType.Bathroom), RoomType.Bathroom),
    new SelectionItem(RoomTypeToString(RoomType.DressingRoom), RoomType.DressingRoom),
    new SelectionItem(RoomTypeToString(RoomType.Hall), RoomType.Hall),
    new SelectionItem(RoomTypeToString(RoomType.Kitchen), RoomType.Kitchen),
    new SelectionItem(RoomTypeToString(RoomType.LivingRoom), RoomType.LivingRoom),
    new SelectionItem(RoomTypeToString(RoomType.Toilet), RoomType.Toilet),
    new SelectionItem(RoomTypeToString(RoomType.Balcony), RoomType.Balcony),
    new SelectionItem(RoomTypeToString(RoomType.Patio), RoomType.Patio),
    new SelectionItem(RoomTypeToString(RoomType.Other), RoomType.Other),
  ];

  newRoomType: RoomType = RoomType.Bedroom;
  addRoom() {
    this._dialogSubscription?.unsubscribe();
    this._dialogSubscription = this._actionWindow
      .open(SelectDialogComponent, this._injector, null, {
        itemsSource: this.roomTypes,
        fieldName: 'DWELLING_INSPECTION.ROOM_TYPE',
        helpText: 'DWELLING_INSPECTION.HELP_TEXT.ROOM_TYPE',
        title: 'DWELLING_INSPECTION.SELECT_ROOM_TYPE',
        defaultValue: RoomType.Bedroom,
      })
      .pipe(
        concatMap(roomType => {
          if (!roomType) {
            return EMPTY;
          }

          return this._componentService.getDefaultComponents(roomType.value).pipe(
            map(res => {
              const components = res ?? [];
              components.forEach(x => (x.id = FakeGuidService.newGuid()));
              return { components, roomType: roomType.value };
            })
          );
        })
      )
      .subscribe(res => {
        const room = new Room();
        room.type = res.roomType;
        room.caption = getTranslation(
          this._languageService.getCurrentTranslations(),
          RoomTypeToString(res.roomType)
        );
        room.components = res.components;
        room.inspectionOrder = this.forms.controls.length + 1;
        this.rentalObject.rooms ??= [];
        this.rentalObject.rooms.push(room);
        this.rooms = this.rentalObject.rooms;
        this.rootForm.markAsDirty();
      });
  }

  ngOnDestroy(): void {
    this._subscription?.unsubscribe();
    this._initSubscription?.unsubscribe();
    this._dialogSubscription?.unsubscribe();
  }

  customProperties: CustomProperty[];

  ngOnInit(): void {
    this._subscription.add(
      this._customPropertyService
        .getList()
        .pipe(
          switchMap(val => {
            this.customProperties = val;
            return this._componentCategoryService.getList();
          })
        )
        .subscribe(list => {
          this.componentCategories = list;
          for (let category of this.componentCategories ?? []) {
            for (let type of category.objectComponentTypes ?? []) {
              for (let property of type.customProperties ?? []) {
                property.property = this.customProperties.find(p => p.id === (property.property as unknown));
              }
            }
          }
          this.rentalObject.rooms ??= [];
          this.rentalObject.rooms.sort((a, b) => a.inspectionOrder - b.inspectionOrder);
          this.rooms = this.rentalObject.rooms;
          for (let room of this.rooms) {
            room.components ??= [];
            for (let component of room.components) {
              component.customPropertyValues ??= [];
              for (let property of component.customPropertyValues) {
                property.customProperty = this.customProperties.find(p => p.id === property.customProperty);
              }
            }
          }
        })
    );
  }

  sort() {
    this._dialogSubscription?.unsubscribe();
    this._dialogSubscription = this._actionWindow
      .open(SortFormComponent, this._injector, null, {
        dataContext: {
          items: this.forms.controls.map(f => new SelectionItem((f as FormGroup).controls['name'].value, f)),
          title: 'DWELLING_INSPECTION.INSPECTION_ORDER',
        },
      })
      .subscribe(result => {
        if (result) {
          let order = 1;
          for (let form of result.map(c => c.value as FormGroup)) {
            this.rooms.find(r => r.id === form.controls['id'].value).inspectionOrder = order;
            form.controls['inspectionOrder'].setValue(order++);
          }
          this.forms.controls.sort(
            (a, b) => (a as FormGroup).controls['inspectionOrder'].value - (b as FormGroup).controls['inspectionOrder'].value
          );

          this.rooms.sort((a, b) => a.inspectionOrder - b.inspectionOrder);
          this.forms.controls.sort(
            (a, b) => (a as FormGroup).controls['inspectionOrder'].value - (b as FormGroup).controls['inspectionOrder'].value
          );
        }
      });
  }

  reorderRoomsSidebarOpen() {
    this.roomsOrderSidebarOpen = true;
  }

  reorderRoomsSidebarClose(revert: boolean) {
    this.roomsOrderSidebarOpen = false;
    if (revert)
      this.rooms.sort((a, b) => a.inspectionOrder - b.inspectionOrder);
  }

  reorderRoomsAccept(rooms: Room[]) {
    // Existing rooms can have duplicate legacy inspection orders, so match their form by the stable room id.
    const formByRoomId = new Map<string, FormGroup>(
      this.forms.controls
        .map(ctrl => ctrl as FormGroup)
        .filter(form => !!form.controls['id'].value)
        .map(form => [form.controls['id'].value, form] as [string, FormGroup])
    );

    // New rooms do not have an id until they have been saved. Their locally assigned order is unique.
    const formByOldOrder = new Map<number, FormGroup>(
      this.forms.controls
        .map(ctrl => ctrl as FormGroup)
        .filter(form => !form.controls['id'].value)
        .map(form => {
          return [form.controls['inspectionOrder'].value, form] as [number, FormGroup];
        })
    );

    // execute reordering
    rooms.forEach((room, index) => {
      const oldOrder = room.inspectionOrder;
      const newOrder = index + 1; // order starts from 1, so we increment index

      room.inspectionOrder = newOrder;

      const fg = room.id ? formByRoomId.get(room.id) : formByOldOrder.get(oldOrder);
      fg?.controls['inspectionOrder'].setValue(newOrder);
    });

    this.rooms.sort((a, b) => a.inspectionOrder - b.inspectionOrder);
    this.rootForm.markAsDirty();
  }
}
